import { createContext, useContext, useEffect, useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';

// Lets a page broadcast "collapse all / expand all" to every CollapsibleCard.
// Provide { signal: number, open: boolean }; bump `signal` to apply `open`.
export const CollapseAllContext = createContext(null);

// A product-page section that collapses, so dense data (fabrics, pricing,
// variants, …) isn't all open at once and can't be edited/deleted by accident.
// Header shows the title + a chevron; click anywhere on the header to toggle.
// `defaultOpen` controls the initial state; `right` renders extra header content
// (e.g. a summary chip) on the right.
const CollapsibleCard = ({ title, children, defaultOpen = false, right = null, mb = 2 }) => {
  const [open, setOpen] = useState(defaultOpen);
  // Respond to a page-level "collapse all / expand all" broadcast.
  const collapseAll = useContext(CollapseAllContext);
  useEffect(() => {
    if (collapseAll && collapseAll.signal > 0) setOpen(collapseAll.open);
  }, [collapseAll?.signal]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Card sx={{ mb }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
          px: 2, py: 1.25, cursor: 'pointer', userSelect: 'none',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={(e) => e.stopPropagation()}>
          {right}
          <IconButton size="small" onClick={() => setOpen((o) => !o)} aria-label={open ? 'Collapse' : 'Expand'}>
            <ExpandMoreRounded sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
          </IconButton>
        </Box>
      </Box>
      <Collapse in={open} unmountOnExit>
        <CardContent sx={{ pt: 0 }}>{children}</CardContent>
      </Collapse>
    </Card>
  );
};

export default CollapsibleCard;
